import { useCallback, useMemo, useRef, useState } from "react";

/**
 * Pengelola tab kerja di dalam satu modul.
 * Tab daftar bersifat permanen; tab data baru dan edit dapat ditutup.
 */
export default function useModuleWorkspaceTabs({
  moduleKey,
  listLabel = "Daftar",
  confirmDiscard,
} = {}) {
  const key = String(moduleKey || "module");
  const listTabId = `${key}:list`;
  const sequence = useRef(0);

  const [tabs, setTabs] = useState(() => [
    {
      id: listTabId,
      type: "list",
      label: listLabel,
      closable: false,
      dirty: false,
    },
  ]);
  const [activeTabId, setActiveTabId] = useState(listTabId);

  const normalizedTabs = useMemo(() => {
    const listTab = tabs.find((tab) => tab.type === "list");
    const otherTabs = tabs.filter((tab) => tab.type !== "list");
    return listTab ? [listTab, ...otherTabs] : otherTabs;
  }, [tabs]);

  const activateTab = useCallback((tabOrId) => {
    const id = typeof tabOrId === "string" ? tabOrId : tabOrId?.id;
    if (!id || !tabs.some((tab) => tab.id === id)) return;
    setActiveTabId(id);
  }, [tabs]);

  const openNewTab = useCallback((options = {}) => {
    const id = `${key}:new:${++sequence.current}`;
    const tab = {
      id,
      type: "new",
      label: options.label || "Data Baru",
      closable: true,
      dirty: false,
      initialData: options.initialData ?? null,
    };

    setTabs((current) => [...current, tab]);
    setActiveTabId(id);
    return id;
  }, [key]);

  const openEditTab = useCallback((recordId, options = {}) => {
    if (recordId === undefined || recordId === null || recordId === "") {
      throw new Error("openEditTab memerlukan ID record yang valid.");
    }

    const id = `${key}:edit:${encodeURIComponent(String(recordId))}`;
    const existing = tabs.find((tab) => tab.id === id);

    if (existing) {
      setActiveTabId(id);
      return id;
    }

    const tab = {
      id,
      type: "edit",
      recordId,
      label: options.label || "Edit Data",
      closable: true,
      dirty: false,
    };

    setTabs((current) => {
      if (current.some((item) => item.id === id)) return current;
      return [...current, tab];
    });
    setActiveTabId(id);
    return id;
  }, [key, tabs]);

  const markTabDirty = useCallback((tabId, dirty = true) => {
    setTabs((current) =>
      current.map((tab) =>
        tab.id === tabId ? { ...tab, dirty: Boolean(dirty) } : tab
      )
    );
  }, []);

  const markTabClean = useCallback((tabId) => {
    markTabDirty(tabId, false);
  }, [markTabDirty]);

  const updateTabLabel = useCallback((tabId, label) => {
    setTabs((current) =>
      current.map((tab) => tab.id === tabId ? { ...tab, label } : tab)
    );
  }, []);

  const closeTab = useCallback((tabOrId, options = {}) => {
    const id = typeof tabOrId === "string" ? tabOrId : tabOrId?.id;
    if (!id || id === listTabId) return false;

    const currentTab = tabs.find((tab) => tab.id === id);
    if (!currentTab || currentTab.type === "list") return false;

    if (currentTab.dirty && !options.skipConfirm) {
      const accepted = confirmDiscard
        ? confirmDiscard(currentTab)
        : window.confirm(
            "Form memiliki perubahan yang belum disimpan. Tutup tab ini?"
          );
      if (!accepted) return false;
    }

    const remaining = tabs.filter((tab) => tab.id !== id);
    const closedIndex = tabs.findIndex((tab) => tab.id === id);

    if (activeTabId === id) {
      const nextTab =
        remaining[closedIndex] ||
        remaining[closedIndex - 1] ||
        remaining.find((tab) => tab.type === "list");
      setActiveTabId(nextTab?.id || listTabId);
    }

    setTabs(remaining);
    return true;
  }, [tabs, activeTabId, listTabId, confirmDiscard]);

  return {
    tabs: normalizedTabs,
    activeTabId,
    activateTab,
    openNewTab,
    openEditTab,
    closeTab,
    markTabDirty,
    markTabClean,
    updateTabLabel,
    listTabId,
  };
}
